import { LucideIcon, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: LucideIcon;
  badge?: {
    text: string;
    variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
  };
  variant?: 'default' | 'success' | 'warning' | 'destructive';
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
}

const MetricCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  badge,
  variant = 'default',
  onRefresh,
  refreshing,
  className
}: MetricCardProps) => {
  const variantStyles = {
    default: 'border-border',
    success: 'border-green-500/20 bg-green-500/5',
    warning: 'border-yellow-500/20 bg-yellow-500/5',
    destructive: 'border-red-500/20 bg-red-500/5'
  };

  return (
    <Card className={cn('hover:shadow-md transition-shadow flex flex-col', variantStyles[variant], className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground truncate whitespace-nowrap">
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 shrink-0">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={refreshing}
              className="h-6 w-6"
            >
              <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col justify-between flex-1">
        <div className="flex items-baseline justify-between">
          <div className="text-2xl font-bold leading-tight">{value}</div>
          {badge && (
            <Badge variant={badge.variant || 'default'} className="ml-2">
              {badge.text}
            </Badge>
          )}
        </div>
        <p className={cn("text-xs text-muted-foreground mt-2 truncate", !subtitle && "invisible")}>
          {subtitle || 'placeholder'}
        </p>
      </CardContent>
    </Card>
  );
};


export default MetricCard;
